import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Tabs, Divider, message } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, IdcardOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginWithPassword, clearError } from '../store/slices/authSlice';
import authService from '../services/authService';

const { Title, Text, Paragraph } = Typography;

const AuthPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
    const [loginForm] = Form.useForm();
    const [registerForm] = Form.useForm();
    const [activeTab, setActiveTab] = useState('login');
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerError, setRegisterError] = useState(null);

    // Get redirect path from location state
    const from = location.state?.from || '/';

    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, from]);

    const handleLogin = async (values) => {
        dispatch(clearError());
        const result = await dispatch(loginWithPassword({
            username: values.username,
            password: values.password
        }));

        if (!result.error) {
            message.success('Muvaffaqiyatli kirdingiz!');
        }
    };

    const handleRegister = async (values) => {
        setRegisterLoading(true);
        setRegisterError(null);

        try {
            const response = await authService.registerWithPassword(
                values.username,
                values.password,
                values.phone_number,
                values.first_name,
                values.last_name
            );

            // Save token and user to localStorage
            localStorage.setItem('token', response.token);
            localStorage.setItem('user', JSON.stringify(response.user));

            message.success("Ro'yxatdan muvaffaqiyatli o'tdingiz!");

            // Reload page to update auth state
            window.location.href = from;
        } catch (err) {
            setRegisterError(err.response?.data?.message || "Ro'yxatdan o'tishda xatolik");
        } finally {
            setRegisterLoading(false);
        }
    };

    const tabItems = [
        {
            key: 'login',
            label: 'Kirish',
            children: (
                <div>
                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 24 }}
                            closable
                            onClose={() => dispatch(clearError())}
                        />
                    )}

                    <Form
                        form={loginForm}
                        layout="vertical"
                        onFinish={handleLogin}
                        autoComplete="off"
                        size="large"
                    >
                        <Form.Item
                            name="username"
                            rules={[{ required: true, message: 'Foydalanuvchi nomini kiriting' }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder="Foydalanuvchi nomi"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: 'Parolni kiriting' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder="Parol"
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 16 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                size="large"
                                style={{ height: 48, fontSize: 16, fontWeight: 600 }}
                            >
                                Kirish
                            </Button>
                        </Form.Item>
                    </Form>

                    <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
                        Hisobingiz yo&apos;qmi?{' '}
                        <Button type="link" onClick={() => setActiveTab('register')} style={{ padding: 0 }}>
                            Ro&apos;yxatdan o&apos;ting
                        </Button>
                    </Text>
                </div>
            ),
        },
        {
            key: 'register',
            label: "Ro'yxatdan o'tish",
            children: (
                <div>
                    {registerError && (
                        <Alert
                            message={registerError}
                            type="error"
                            showIcon
                            style={{ marginBottom: 24 }}
                            closable
                            onClose={() => setRegisterError(null)}
                        />
                    )}

                    <Form
                        form={registerForm}
                        layout="vertical"
                        onFinish={handleRegister}
                        autoComplete="off"
                        size="large"
                    >
                        <Form.Item
                            name="username"
                            rules={[
                                { required: true, message: 'Foydalanuvchi nomini kiriting' },
                                { min: 3, message: 'Kamida 3 ta belgi' },
                            ]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder="Foydalanuvchi nomi *"
                            />
                        </Form.Item>

                        <Form.Item
                            name="phone_number"
                            rules={[
                                { required: true, message: 'Telefon raqamini kiriting' },
                                { pattern: /^\+?[0-9]{9,15}$/, message: "To'g'ri telefon raqam kiriting" },
                            ]}
                        >
                            <Input
                                prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder="Telefon raqami * (+998901234567)"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[
                                { required: true, message: 'Parolni kiriting' },
                                { min: 6, message: 'Kamida 6 ta belgi' },
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder="Parol * (kamida 6 ta belgi)"
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirm_password"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: 'Parolni tasdiqlang' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Parollar mos kelmaydi'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder="Parolni tasdiqlang *"
                            />
                        </Form.Item>

                        <Divider style={{ margin: '16px 0' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Ixtiyoriy</Text>
                        </Divider>

                        <div style={{ display: 'flex', gap: 12 }}>
                            <Form.Item name="first_name" style={{ flex: 1, marginBottom: 16 }}>
                                <Input
                                    prefix={<IdcardOutlined style={{ color: '#bfbfbf' }} />}
                                    placeholder="Ism"
                                />
                            </Form.Item>

                            <Form.Item name="last_name" style={{ flex: 1, marginBottom: 16 }}>
                                <Input
                                    placeholder="Familiya"
                                />
                            </Form.Item>
                        </div>

                        <Form.Item style={{ marginBottom: 16 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={registerLoading}
                                block
                                size="large"
                                style={{ height: 48, fontSize: 16, fontWeight: 600 }}
                            >
                                Ro&apos;yxatdan o&apos;tish
                            </Button>
                        </Form.Item>
                    </Form>

                    <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
                        Hisobingiz bormi?{' '}
                        <Button type="link" onClick={() => setActiveTab('login')} style={{ padding: 0 }}>
                            Kirish
                        </Button>
                    </Text>
                </div>
            ),
        },
    ];

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            padding: 24,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            position: 'relative'
        }}>
            <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/')}
                style={{
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    borderRadius: 20,
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
            >
                Bosh sahifaga qaytish
            </Button>
            <Card
                style={{
                    width: '100%',
                    maxWidth: 420,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    borderRadius: 16,
                    border: 'none',
                }}
                bodyStyle={{ padding: '32px 28px' }}
            >
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <Title level={2} style={{ margin: 0, background: 'linear-gradient(135deg, #667eea, #764ba2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        🛒 Megadent Shop
                    </Title>
                    <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                        Xush kelibsiz! Davom etish uchun tizimga kiring
                    </Paragraph>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    centered
                    style={{ marginBottom: 0 }}
                />

                <Divider style={{ margin: '24px 0 16px' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>yoki</Text>
                </Divider>

                <div style={{ textAlign: 'center' }}>
                    <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                        Telegram orqali tezroq kiring
                    </Paragraph>
                    <Button
                        size="large"
                        icon={<span style={{ marginRight: 8 }}>📱</span>}
                        onClick={() => window.open('https://t.me/megadentshop_bot', '_blank')}
                        style={{
                            height: 44,
                            borderRadius: 8,
                            background: '#0088cc',
                            color: 'white',
                            border: 'none',
                            fontWeight: 500,
                        }}
                    >
                        Telegram Bot orqali kirish
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default AuthPage;
